import React, { useCallback, useState } from "react";
import { Modal, Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as Yup from "yup";
import api from "../../../services/api";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import getValidationErrors from "../../../utils/getValidationErrors";

interface IDisapproveModalFormData {
  reason: string;
}

interface IApprovalData {
  approved: boolean;
  reason: string;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const DisapprovalModal: React.FC<IActionModalProps> = ({
  project,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();

  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description =
            "O projeto tem que estar na fase de aguardo de aprovação para ser reprovado";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description =
            "Um erro occoreu ao reprovar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao reprovar",
        description,
      });
    },
    [addToast]
  );

  const handleSendRequest = useCallback(
    async (payload: IApprovalData) => {
      try {
        setLoading(true);

        const response = await api.patch(
          `/projects/${project.id}/approve-disapprove`,
          payload
        );

        handleUpdateProject(response.data);
        addToast({
          type: "success",
          title: "Projeto reprovado",
          description: "O projeto foi reprovado com sucesso",
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleUpdateProject, project.id]
  );

  const handleSubmit = useCallback(
    async (data: IDisapproveModalFormData) => {
      try {
        const schema = Yup.object().shape({
          reason: Yup.string().required("Motivo obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { reason } = data;

        const payload = {
          approved: false,
          reason,
        };

        handleSendRequest(payload);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [handleSendRequest]
  );

  return (
    <>
      <Modal
        title={t("disapprove")}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        forceRender
      >
        <Form
          {...layout}
          form={form}
          name="approve-form"
          onFinish={handleSubmit}
        >
          <FormItem
            name="reason"
            required
            label={t("reason")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        </Form>
      </Modal>
      <Button
        type="primary"
        danger
        onClick={() => setVisible((oldVisible) => !oldVisible)}
      >
        {t("disapprove")}
      </Button>
    </>
  );
};

export default DisapprovalModal;
